/**
 * Text the SERVER writes that a reader sees: dashboard figures and their
 * labels, Needs Attention lines, report summaries and notes, notification
 * titles and bodies, refusal messages. The server has no reader language —
 * a cron writes a notification for ten people — so it keeps writing English,
 * and the client translates what arrives.
 *
 * Each sentence is declared ONCE, as a msg(), in a catalogue under
 * shared/serverText/. The server builds its text from `.en` (through
 * `serverText()` below, so placeholders are filled the same way everywhere);
 * the client hands whatever it received to `localizeServerText()`, which finds
 * the template that produced it — placeholders included, so
 * "3 deposits due this week" is recognised as "{count} deposits due this week"
 * with count = 3 — and renders it in the reader's language. Text no catalogue
 * knows (a name, a vendor's message, a row written before the catalogue
 * existed) is shown exactly as received.
 *
 * That matching is also what translates notifications ALREADY stored in the
 * database: they were produced from the same templates.
 */
import { interpolate, type Msg, type MsgVars } from "./i18n";

/** Fill a server template — the one way server code turns a msg into English. */
export function serverText(m: Msg, vars?: MsgVars): string {
  return interpolate(m.en, vars);
}

type Compiled = { m: Msg; re: RegExp; names: string[]; literal: boolean };

let compiled: Compiled[] | null = null;
let exact: Map<string, Msg> | null = null;

/*
  The catalogues are REGISTERED, not imported here: together they are tens of
  kilobytes, and this module is reached from the toaster and every error card,
  so a static import put them in the first-load bundle of every English reader,
  who never needs them. The server registers them at start (server/lib/
  serverI18n.ts); the client loads them in their own chunk the first time the
  reader's language is not English (useTranslation → loadServerTextCatalogues).
  Until then, text passes through unchanged — which is exactly right in English.
*/
let catalogues: ReadonlyArray<Readonly<Record<string, Msg>>> = [];

export function registerServerTextCatalogues(list: ReadonlyArray<Readonly<Record<string, Msg>>>): void {
  catalogues = list;
  compiled = null;
  exact = null;
}

let loading: Promise<void> | null = null;
/** Client: fetch and register every catalogue, once. */
export function loadServerTextCatalogues(): Promise<void> {
  loading ??= import("./serverText/index").then((m) => registerServerTextCatalogues(m.SERVER_TEXT_CATALOGUES));
  return loading;
}

function escape(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function compile(): void {
  compiled = [];
  exact = new Map();
  for (const catalogue of catalogues) {
    for (const m of Object.values(catalogue)) {
      const names: string[] = [];
      const parts = m.en.split(/(\{\w+\})/);
      if (parts.length === 1) {
        exact.set(m.en, m);
        continue;
      }
      const pattern = parts
        .map((p) => {
          const v = /^\{(\w+)\}$/.exec(p);
          if (!v) return escape(p);
          names.push(v[1]);
          return "([\\s\\S]+?)";
        })
        .join("");
      compiled.push({ m, re: new RegExp(`^${pattern}$`), names, literal: false });
    }
  }
  // Most specific first: the template with the most fixed text wins a tie.
  compiled.sort((a, b) => b.m.en.replace(/\{\w+\}/g, "").length - a.m.en.replace(/\{\w+\}/g, "").length);
}

export type ServerTextTranslate = (m: Msg, vars?: MsgVars) => string;

/**
 * Text from the server, in the reader's language when a catalogue produced it.
 * Placeholder values are passed through as received (a name, a figure the
 * server already formatted).
 */
export function localizeServerText(text: string | null | undefined, t: ServerTextTranslate): string {
  if (text === null || text === undefined || text === "") return text ?? "";
  if (!compiled) compile();
  const hit = exact!.get(text);
  if (hit) return t(hit);
  for (const c of compiled!) {
    const match = c.re.exec(text);
    if (!match) continue;
    const vars: MsgVars = {};
    c.names.forEach((n, i) => (vars[n] = match[i + 1]));
    return t(c.m, vars);
  }
  return text;
}

/**
 * Several server sentences joined by spaces (a timeline note): each sentence
 * translated on its own, unknown ones left as written.
 */
export function localizeServerParagraph(text: string | null | undefined, t: ServerTextTranslate): string {
  if (!text) return text ?? "";
  const whole = localizeServerText(text, t);
  if (whole !== text) return whole;
  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => localizeServerText(sentence, t))
    .join(" ");
}
